import { arrayToMap } from 'lib/array/arrayUtils'

/**
 * Display labels for a traveller in the checkout UI.
 * Used to correctly show "Primary traveller" and "Guest" tags per acceptance criteria.
 */
export interface TravellerDisplayLabels {
  /** Show the "Primary traveller" tag */
  isPrimaryTraveller: boolean
  /** Show the "Guest" tag - only for actual guests, never for employees */
  showGuestTag: boolean
}

/**
 * Computes the correct display labels for a traveller in a multi-room LEBT booking.
 *
 * Acceptance criteria:
 * - There can only be one Primary Traveller per room
 * - Guests are only Primary Traveller if they are the only traveller in the room
 * - A guest who is primary (only traveller in room) gets both 'Guest' and 'Primary Traveller' tags
 * - An employee should never get the 'Guest' tag, even if not primary
 * - If employees share a room, the first in employeeIds is primary
 * - Primary is first in [...employeeIds, ...guestIds] (employees before guests)
 *
 * @param occupancy - Room occupancy with employeeIds and guestIds
 * @param travellerForm - The traveller form (has employeeId or guestId, type)
 * @returns Display labels for the traveller
 */
export function getTravellerDisplayLabels(
  occupancy: App.Occupants | App.RoomOccupants | undefined,
  travellerForm: App.Checkout.TravellerForm | undefined,
): TravellerDisplayLabels {
  const isEmployee = travellerForm?.type === 'employee'
  const isGuest = travellerForm?.type === 'guest'

  if (!occupancy || (!isEmployee && !isGuest)) {
    return { isPrimaryTraveller: false, showGuestTag: false }
  }

  const roomTravellerIds = [
    ...(occupancy.employeeIds ?? []),
    ...(occupancy.guestIds ?? []),
  ]

  const travellerId = travellerForm?.employeeId ?? travellerForm?.guestId
  if (!travellerId || !roomTravellerIds.includes(travellerId)) {
    return { isPrimaryTraveller: false, showGuestTag: isGuest }
  }

  const primaryTravellerId = roomTravellerIds[0]
  const isPrimaryTraveller = travellerId === primaryTravellerId

  return {
    isPrimaryTraveller,
    showGuestTag: isGuest,
  }
}

/**
 * Sorts traveller form IDs so they match the room's occupancy order
 * (primary traveller first). Travellers are ordered by their position
 * in [...employeeIds, ...guestIds]. Any traveller not found in the
 * occupancy is placed at the end.
 *
 * @param travellerIds - Array of traveller form IDs (e.g. "adult-1", "adult-3")
 * @param occupancy - Room occupancy with employeeIds and guestIds
 * @param formDataById - Map of form ID to traveller form (to resolve employeeId/guestId)
 * @returns A new sorted array of traveller form IDs
 */
export function sortTravellerIdsByOccupancy(
  travellerIds: Array<string>,
  occupancy: App.Occupants | App.RoomOccupants,
  formDataById: Record<string, App.Checkout.TravellerForm | undefined>,
): Array<string> {
  const occupancyOrder = [
    ...(occupancy.employeeIds ?? []),
    ...(occupancy.guestIds ?? []),
  ]

  return [...travellerIds].sort((a, b) => {
    const formA = formDataById[a]
    const formB = formDataById[b]
    const idA = formA?.employeeId ?? formA?.guestId
    const idB = formB?.employeeId ?? formB?.guestId
    const indexA = idA ? occupancyOrder.indexOf(idA) : -1
    const indexB = idB ? occupancyOrder.indexOf(idB) : -1
    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
  })
}

/**
 * Checkout label for a room, e.g. "Room 1" or "Room 1 · Twin Room".
 */
export function getRoomAssignmentLabel(roomIndex: number, roomTypeName?: string): string {
  const roomLabel = `Room ${roomIndex + 1}`
  return roomTypeName ? `${roomLabel} · ${roomTypeName}` : roomLabel
}

/**
 * Heading for a traveller-details group on multi-room checkout.
 * Uses the group key when the group is not room-based (e.g. cruise), otherwise
 * resolves the room type from the cart item at `roomIndex`.
 */
export function getGroupRoomHeading(
  groupKey: string,
  roomIndex: number | undefined,
  accommodationItems: Array<App.Checkout.LEHotelItem | App.Checkout.BedbankHotelItem>,
  roomTypeNamesByItemId: Map<string, string>,
): string {
  if (roomIndex === undefined) {
    return groupKey
  }
  return getRoomAssignmentLabel(
    roomIndex,
    roomTypeNamesByItemId.get(accommodationItems[roomIndex]?.itemId ?? ''),
  )
}

/**
 * Maps checkout accommodation item IDs to their room type title.
 */
export function getRoomTypeNameByItemId(
  offerViews: Array<App.Checkout.AccommodationOfferView>,
): Map<string, string> {
  const itemViews = offerViews.flatMap(
    (offerView): Array<App.Checkout.AccommodationItemView> => offerView.itemViews,
  )
  return arrayToMap(
    itemViews.filter((itemView): itemView is App.Checkout.AccommodationItemView & { primaryTitle: string } =>
      !!itemView.primaryTitle,
    ),
    itemView => itemView.item.itemId,
    itemView => itemView.primaryTitle,
  )
}

/**
 * Reorders `ids` so that any IDs present in `originalOrder` come first
 * (in their original relative order), followed by new IDs not in `originalOrder`.
 * Used to preserve the existing primary traveller when rooms are reassigned.
 *
 * @param ids - The current set of IDs to reorder
 * @param originalOrder - The previous ordering to preserve
 * @returns A new array with retained IDs first, then newly added IDs
 */
export function stableReorder(ids: Array<string>, originalOrder: Array<string>): Array<string> {
  const idsSet = new Set(ids)
  const originalSet = new Set(originalOrder)
  const retained = originalOrder.filter(id => idsSet.has(id))
  const added = ids.filter(id => !originalSet.has(id))
  return [...retained, ...added]
}

/**
 * Helper to check if a room should be included in business credits calculation.
 *
 * Returns true if:
 * - Room allocation has NOT been done yet (no employeeIds AND no guestIds) - assume employees will use the room
 * - Room has at least one ACTIVATED employee assigned (an employee with customerId)
 *
 * Returns false if:
 * - Room allocation has been done AND room has ONLY guest travellers or AVAILABLE employees
 *
 * AVAILABLE employees (those without customerId) do not earn credits, so a room with only
 * AVAILABLE employees is treated the same as a guest-only room for credit purposes.
 *
 * @param itemView - The accommodation item view
 * @param employees - Optional map of employee records keyed by ID. When provided, used to
 *                    distinguish ACTIVATED (has customerId) from AVAILABLE employees.
 *                    When not provided, falls back to checking any employee presence.
 */
export function roomHasEmployeeTravellers(
  itemView: App.Checkout.AccommodationItemView,
  employees?: Record<string, App.BusinessTraveller.EmployeeFromMeEndpoint>,
): boolean {
  const occupancy = itemView.item.occupancy
  if (!occupancy) {
    return true
  }

  const employeeIds = occupancy.employeeIds
  const guestIds = occupancy.guestIds

  const hasGuests = Array.isArray(guestIds) && guestIds.length > 0

  if (employees) {
    const hasActivatedEmployees = Array.isArray(employeeIds) && employeeIds.some(
      id => id && id.trim().length > 0 && employees[id]?.customerId,
    )

    if (!hasActivatedEmployees && !hasGuests) {
      const hasAnyEmployees = Array.isArray(employeeIds) && employeeIds.some(id => id && id.trim().length > 0)
      return !hasAnyEmployees
    }

    return hasActivatedEmployees
  }

  // Fallback: no employees map provided, use original behavior
  const hasEmployees = Array.isArray(employeeIds) && employeeIds.some(id => id && id.trim().length > 0)

  if (!hasEmployees && !hasGuests) {
    return true
  }

  return hasEmployees
}
